// Modal componenet based on Buefy Modal from https://github.com/rafaelpimpa/buefy
<template>
  <!-- The anchor stays where the modal is declared. The portal is moved to <body> on mount so the modal
       escapes ancestor stacking contexts (.app-content, .quote-body) and covers the AppNav and sticky headers.
       Only the portal moves, never the root, so the parent's DOM patching never references a node in <body>. -->
  <div class="base-modal-ex-anchor">
    <div ref="portal"
      class="base-modal-ex-portal">
      <transition name="custom-classes-transition"
        :enter-active-class="`animate__animated animate__${animationIn}`"
        :leave-active-class="`animate__animated animate__${animationOut}`">
        <div class="modal is-active"
          :class="modalClass"
          v-if="isActive"
          :style="`animation-duration: ${animationDuration}s`">
          <div class="modal-background"
            @click="cancel" />
          <div :class="{ 'modal-content': !hasModalCard, 'modal-card': hasModalCard }"
            :style="{ 'min-width': `${minWidth}vw`, 'min-height' : `${minHeight}vh`, 'max-height' : `${maxHeight}vh` }">
            <component v-if="component"
              v-bind="props"
              :is="component"
              @close="close" />
            <div v-else-if="content"
              v-html="content" />
            <slot v-else />
          </div>
          <button v-if="canCancel"
            class="modal-close is-large"
            @click="cancel" />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BulmaModal',
  props: {
    active: {
      type: Boolean,
      default: false
    },
    component: Object,
    content: String,
    programmatic: Boolean,
    props: Object,
    minWidth: {
      type: [String, Number],
      default: 30
    },
    minHeight: {
      type: [String, Number],
      default: 30
    },
    maxHeight: {
      type: [String, Number],
      default: 90
    },
    hasModalCard: Boolean,
    // Classes for the .modal element. A class on <base-modal-ex> lands on the hidden anchor, not the modal
    modalClass: {
      type: [String, Array, Object],
      default: ''
    },
    canCancel: {
      type: Boolean,
      default: true
    },
    onCancel: {
      type: Function,
      default: () => {}
    },
    animationIn: {
      type: String,
      default: 'zoomIn'
    },
    animationOut: {
      type: String,
      default: 'zoomOut'
    },
    animationDuration: {
      type: [String, Number],
      default: 0.3
    }
  },
  data() {
    return {
      isActive: this.active || false,
      newWidth: typeof this.width === 'number' ? this.width + 'px' : this.width
    }
  },
  watch: {
    active(value) {
      this.isActive = value
    },
    isActive() {
      if (typeof window !== 'undefined') {
        const action = this.isActive ? 'add' : 'remove'
        document.documentElement.classList[action]('is-clipped')
      }
      // Re-append on open so the most recently opened modal stacks above any other open modal
      if (this.isActive) this.moveToBody()
    }
  },
  created() {
    if (typeof window !== 'undefined') {
      document.addEventListener('keyup', this.keyPress)
    }
  },
  mounted() {
    this.moveToBody()
    if (this.programmatic) this.isActive = true
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      document.removeEventListener('keyup', this.keyPress)
      if (this.isActive) document.documentElement.classList.remove('is-clipped')
    }
    // Vue only removes the anchor (or an ancestor of it), so the portal in <body> must be removed here
    this.removeFromBody()
  },
  methods: {
    /**
     * Move the portal to the end of <body>.
     */
    moveToBody() {
      const portal = this.$refs.portal
      if (portal && portal !== document.body.lastElementChild) {
        document.body.appendChild(portal)
      }
    },
    removeFromBody() {
      const portal = this.$refs.portal
      if (portal && portal.parentNode) {
        portal.parentNode.removeChild(portal)
      }
    },
    /**
     * Close the Modal if canCancel.
     */
    cancel() {
      if (!this.canCancel) return
      this.close()
    },
    /**
     * Call the onCancel prop (function).
     * Emit events, and destroy modal if it's programmatic.
     */
    close() {
      this.onCancel.apply(null, arguments)
      this.$emit('close')
      this.$emit('update:active', false)
      // Timeout for the animation complete before destroying
      if (this.programmatic) {
        this.isActive = false
        setTimeout(() => {
          this.$destroy()
          this.$el.remove()
        }, 150)
      }
    },
    /**
     * Keypress event that is bound to the document.
     */
    keyPress(event) {
      // Esc key
      if (event.keyCode === 27) this.cancel()
    }
  }
}
</script>

<style lang="scss" scoped>
.base-modal-ex-anchor {
  display: none;
}
.modal-card {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, calc(-50% + 20px));
}
</style>

